import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  FiEdit2,
  FiPlus,
  FiRefreshCcw,
  FiShield,
  FiUsers,
} from "react-icons/fi";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";

import { selectStore } from "../../../store/selectors/StoreSelector";
import { WSUsersInstance } from "../../../services/remote/users/WSUsers";
import { User, UserRole } from "../../../entity/User";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import WSHR from "../../../services/remote/hr/WSHR";
import { Contract } from "../../../entity/Contract";
import { IoIosArrowForward } from "react-icons/io";
import { COLORS } from "../../../utils/Colors";
import { useNavigate } from "react-router-dom";
import { VRoutes } from "../../../navigation/Routes";
import { VParamsRoutes } from "../../../navigation/VParams";

const roleOptions = [
  //   {
  //     value: UserRole.ADMIN,
  //     label: "Admin",
  //     color: "red",
  //   },
  //   {
  //     value: UserRole.SUBADMIN,
  //     label: "Sub Admin",
  //     color: "orange",
  //   },
  //   {
  //     value: UserRole.USER,
  //     label: "User",
  //     color: "gray",
  //   },
  {
    value: UserRole.STORE_ADMIN,
    label: t("team.store_admin"),
    color: "purple",
  },
  {
    value: UserRole.STORE_EDITOR,
    label: t("team.store_editor"),
    color: "blue",
  },
  {
    value: UserRole.STORE_CONFIRMER,
    label: t("team.store_confirmer"),
    color: "green",
  },
];

const emptyUser: User = {
  email: "",
  role: UserRole.USER,
  isActive: true,
};

export const TeamScreen: React.FC = () => {
  const store = useSelector(selectStore);
  const WSHRInstance = WSHR;

  const toast = useToast();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [users, setUsers] = useState<User[]>([]);
  const [user, setUser] = useState<User>(emptyUser);
  const [contracts, setContracts] = useState<Contract[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const bg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  const activeUsers = useMemo(
    () => users.filter((u) => u.isActive).length,
    [users],
  );

  const adminUsers = useMemo(
    () =>
      users.filter((u) => [UserRole.ADMIN, UserRole.SUBADMIN].includes(u.role))
        .length,
    [users],
  );

  const getRoleConfig = (role: UserRole) => {
    return (
      roleOptions.find((r) => r.value === role) || {
        label: role,
        color: "gray",
      }
    );
  };

  const getUsersByStore = async () => {
    try {
      setIsLoading(true);

      const response = await WSUsersInstance.getUsersByStore(store?.id || "");

      setUsers(response);
    } catch (error) {
      toast({
        title: t("error"),
        description: t("team.unable_to_fetch_team_members"),
        status: "error",
      });
    } finally {
    }
  };

  const getContractsByStoreId = async () => {
    try {
      const response = await WSHRInstance.getContractsByStoreId(store!.id);

      setContracts(response);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setUser({
      ...emptyUser,
      storeId: store?.id,
    });

    onOpen();
  };

  const handleEdit = (selectedUser: User) => {
    setUser({
      ...selectedUser,
      password: "",
    });

    onOpen();
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      if (user.id) {
        await WSUsersInstance.updateMember(user);

        toast({
          title: t("updated"),
          description: t("team.user_updated_successfully"),
          status: "success",
        });
      } else {
        await WSUsersInstance.createNewMember({
          ...user,
          storeId: store?.id,
        });

        toast({
          title: t("created"),
          description: t("team.user_created_successfully"),
          status: "success",
        });
      }

      onClose();
      getUsersByStore();
    } catch (error) {
      toast({
        title: t("error"),
        description: t("team.failed_to_save_user"),
        status: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (store?.id && user) {
      getUsersByStore();
      getContractsByStoreId();
    }
  }, [store]);

  const handleMemberClick = (userId: string) => {
    navigate(`${VRoutes.DASHBOARD}${VRoutes.STORE_TEAM_MEMBER}${userId}`);
  };

  return (
    <Box bg={bg} minH="100vh" p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex
          justify="space-between"
          align={{ base: "start", md: "center" }}
          direction={{ base: "column", md: "row" }}
          gap={4}
        >
          <Box>
            <Text fontSize="3xl" fontWeight="bold">
              {t("team.team_management")}
            </Text>

            <Text color="gray.500" mt={1}>
              {t("team.manage_members_and_permissions_for") + " " + store?.name}
            </Text>
          </Box>

          <HStack spacing={3}>
            <IconButton
              aria-label="Refresh"
              icon={<FiRefreshCcw />}
              onClick={getUsersByStore}
              variant="outline"
              rounded="xl"
            />

            <Button
              leftIcon={<FiPlus />}
              colorScheme="blue"
              rounded="xl"
              px={6}
              onClick={handleCreate}
            >
              {t("team.add_member")}
            </Button>
          </HStack>
        </Flex>

        {/* Stats */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>
          <StatCard
            title={t("team.total_members")}
            value={users.length}
            icon={<FiUsers />}
          />

          <StatCard
            title={t("team.active_users")}
            value={activeUsers}
            icon={<FiShield />}
          />

          <StatCard
            title={t("team.admins")}
            value={adminUsers}
            icon={<FiShield />}
          />
        </SimpleGrid>

        {/* Table */}
        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          rounded="2xl"
          overflow="hidden"
          shadow="sm"
        >
          <Flex
            p={5}
            justify="space-between"
            align="center"
            borderBottomWidth="1px"
            borderColor={borderColor}
          >
            <Box>
              <Text fontSize="lg" fontWeight="bold">
                {t("team.team_members")}
              </Text>

              <Text fontSize="sm" color="gray.500">
                {t("team.users_with_access_to_this_store")}
              </Text>
            </Box>
          </Flex>

          {isLoading ? (
            <Flex py={20} justify="center">
              <Spinner size="lg" />
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>{t("team.user")}</Th>

                    <Th>{t("team.role")}</Th>
                    <Th>{t("team.contract")}</Th>
                    <Th>{t("team.status")}</Th>
                    <Th>{t("team.created")}</Th>
                    <Th textAlign="right">{t("team.actions")}</Th>
                  </Tr>
                </Thead>

                <Tbody>
                  {users.map((member) => {
                    const role = getRoleConfig(member.role);

                    return (
                      <Tr key={member.id}>
                        <Td>
                          <HStack>
                            <Avatar name={member.email} size="md" />

                            <Box>
                              <Text fontWeight="600">{member.email}</Text>

                              <Text fontSize="sm" color="gray.500">
                                {member.email}
                              </Text>
                            </Box>
                          </HStack>
                        </Td>

                        <Td>
                          <Badge
                            colorScheme={role.color}
                            rounded="full"
                            px={3}
                            py={1}
                            textTransform="capitalize"
                          >
                            {role.label}
                          </Badge>
                        </Td>

                        <Td>
                          <Text fontSize="sm" color="gray.500">
                            {t(
                              "team.contract_type." +
                                contracts.find((c) => c.userId === member.id)
                                  ?.contractType,
                            )}
                          </Text>
                        </Td>

                        <Td>
                          <Badge
                            colorScheme={member.isActive ? "green" : "red"}
                            rounded="full"
                            px={3}
                            py={1}
                          >
                            {member.isActive
                              ? t("team.active")
                              : t("team.inactive")}
                          </Badge>
                        </Td>

                        <Td>
                          <Text fontSize="sm" color="gray.500">
                            {member.createdAt
                              ? new Date(member.createdAt).toLocaleDateString()
                              : "-"}
                          </Text>
                        </Td>

                        <Td textAlign="right">
                          <IconButton
                            aria-label={t("team.edit_user")}
                            icon={<FiEdit2 />}
                            variant="ghost"
                            rounded="xl"
                            onClick={() => handleEdit(member)}
                          />
                        </Td>

                        <Td>
                          <IoIosArrowForward
                            style={{ cursor: "pointer" }}
                            color={COLORS.Neutral.Gray}
                            onClick={() => handleMemberClick(member.id || "")}
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>
      </VStack>

      {/* Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
        <DrawerOverlay />

        <DrawerContent>
          <DrawerCloseButton />

          <DrawerHeader borderBottomWidth="1px">
            {user.id ? t("team.update_member") : t("team.create_member")}
          </DrawerHeader>

          <DrawerBody>
            <Stack spacing={6} mt={4}>
              <FormControl isRequired>
                <FormLabel>{t("team.email")}</FormLabel>

                <Input
                  placeholder="john@company.com"
                  value={user.email}
                  onChange={(e) =>
                    setUser((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  rounded="xl"
                />
              </FormControl>

              <FormControl>
                <FormLabel>{t("team.password")}</FormLabel>

                <Input
                  type="password"
                  placeholder={
                    user.id
                      ? "Leave empty to keep current password"
                      : "Enter password"
                  }
                  value={user.password || ""}
                  onChange={(e) =>
                    setUser((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  rounded="xl"
                />
              </FormControl>

              <FormControl>
                <FormLabel>{t("team.role")}</FormLabel>

                <Select
                  value={user.role}
                  onChange={(e) =>
                    setUser((prev) => ({
                      ...prev,
                      role: e.target.value as UserRole,
                    }))
                  }
                  rounded="xl"
                >
                  {roleOptions.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <Divider />

              <Grid templateColumns="1fr auto" alignItems="center" gap={4}>
                <GridItem>
                  <Text fontWeight="600">{t("team.active_user")}</Text>

                  <Text fontSize="sm" color="gray.500">
                    {t("team.disable_access_without_deleting_user")}
                  </Text>
                </GridItem>

                <GridItem>
                  <Switch
                    isChecked={user.isActive}
                    onChange={(e) =>
                      setUser((prev) => ({
                        ...prev,
                        isActive: e.target.checked,
                      }))
                    }
                  />
                </GridItem>
              </Grid>
            </Stack>
          </DrawerBody>

          <DrawerFooter borderTopWidth="1px">
            <HStack w="full" spacing={3}>
              <Button w="full" variant="outline" rounded="xl" onClick={onClose}>
                {t("cancel")}
              </Button>

              <Button
                w="full"
                colorScheme="blue"
                rounded="xl"
                onClick={handleSave}
                isLoading={isSaving}
              >
                {user.id ? t("update") : t("create")}
              </Button>
            </HStack>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon }) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  return (
    <Flex
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      rounded="2xl"
      p={5}
      justify="space-between"
      align="center"
      shadow="sm"
    >
      <Box>
        <Text fontSize="sm" color="gray.500">
          {title}
        </Text>

        <Text fontSize="3xl" fontWeight="bold" mt={1}>
          {value}
        </Text>
      </Box>

      <Flex
        w="52px"
        h="52px"
        align="center"
        justify="center"
        rounded="2xl"
        bg="blue.50"
        fontSize="22px"
      >
        {icon}
      </Flex>
    </Flex>
  );
};
